import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { EventExtraSettingsDTO } from '../../../services/component-data-source/data/event/event_extra';
import { EventDTO } from '../../../dto/event/event.dto';

interface EventCategoryColorItem {
  category: string;
  color: string;
}

interface EventExtraFormModel {
  category: string | null;
  cleaning: boolean;
  certificates_created: boolean;
}

/** Felter der ikke længere redigeres i UI, men sendes uændret ved gem. */
interface EventExtraPreservedFields {
  smoke_development: boolean;
  cash_list_required: boolean;
  cash_list_packed_date: string | null;
}

const DEFAULT_MODEL: EventExtraFormModel = {
  category: null,
  cleaning: false,
  certificates_created: false,
};

const DEFAULT_PRESERVED: EventExtraPreservedFields = {
  smoke_development: false,
  cash_list_required: false,
  cash_list_packed_date: null,
};

@Component({
  selector: 'app-duna-event-extra',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
  ],
  templateUrl: './duna-event-extra.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-event-extra.component.scss',
})
export class DunaEventExtraComponent implements OnInit, OnDestroy {
  model: EventExtraFormModel = { ...DEFAULT_MODEL };
  private preservedExtra: EventExtraPreservedFields = {
    ...DEFAULT_PRESERVED,
  };
  categoryOptions: EventCategoryColorItem[] = [];
  loading = false;
  saving = false;
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private dataSourceService: ComponentDataSourceService,
    private dataDestService: ComponentDataDestService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedCategoryColor(): string | null {
    const selected = this.categoryOptions.find(
      (opt) => opt.category === this.model.category
    );
    return selected?.color ?? null;
  }

  load(): void {
    const event$ = this.dataSourceService.executeDataSource('event');
    const extra$ = this.dataSourceService.executeDataSource('event_extra');

    if (!event$ || !extra$) {
      this.errorMessage =
        'Kunne ikke initialisere data-kilder for event-indstillinger.';
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    const eventObservable = event$ as Observable<EventDTO>;
    const extraObservable = extra$ as Observable<EventExtraSettingsDTO>;

    forkJoin({
      event: eventObservable,
      extra: extraObservable,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ event, extra }) => {
          this.model = this.normalizeExtra(extra);
          const orgId = event?.organization?.id ?? null;
          this.loadOrgCategoryOptions(orgId);
        },
        error: (err) => {
          this.loading = false;
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Kunne ikke hente event-indstillinger.';
          this.cdr.detectChanges();
        },
      });
  }

  save(): void {
    const dest = this.dataDestService.getDataDest('update_event_extra');
    if (!dest) {
      this.errorMessage = 'Gem-funktion er ikke tilgængelig.';
      this.cdr.detectChanges();
      return;
    }

    const payload = {
      smoke_development: this.preservedExtra.smoke_development,
      cleaning: !!this.model.cleaning,
      cash_list_required: this.preservedExtra.cash_list_required,
      certificates_created: !!this.model.certificates_created,
      cash_list_packed_date: this.preservedExtra.cash_list_packed_date,
    };

    this.saving = true;
    this.errorMessage = null;
    const obs = dest(payload) as any;
    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.saving = false;
        this.errorMessage =
          err?.error?.message ||
          err?.message ||
          'Kunne ikke gemme event-indstillinger.';
        this.cdr.detectChanges();
      },
    });
  }

  trackByCategory(_: number, item: EventCategoryColorItem): string {
    return item.category;
  }

  private loadOrgCategoryOptions(orgId: number | null): void {
    if (orgId == null) {
      this.categoryOptions = [];
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    const orgSettings$ = this.dataSourceService.executeDataSource(
      'org_settings',
      { organizationId: orgId }
    );
    if (!orgSettings$) {
      this.categoryOptions = [];
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    (orgSettings$ as any)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of({ data: {} }))
      )
      .subscribe((res: { data?: Record<string, unknown> }) => {
        const colors = this.normalizeCategoryColors(
          res?.data?.['event_category_colors']
        );
        this.categoryOptions = colors;

        if (
          this.model.category &&
          !colors.some((c) => c.category === this.model.category)
        ) {
          this.model.category = null;
        }

        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  private normalizeExtra(
    extra: EventExtraSettingsDTO | null | undefined
  ): EventExtraFormModel {
    if (!extra) {
      this.preservedExtra = { ...DEFAULT_PRESERVED };
      return { ...DEFAULT_MODEL };
    }

    this.preservedExtra = {
      smoke_development: !!extra.smoke_development,
      cash_list_required: !!extra.cash_list_required,
      cash_list_packed_date: extra.cash_list_packed_date || null,
    };

    return {
      category: extra.category || null,
      cleaning: !!extra.cleaning,
      certificates_created: !!extra.certificates_created,
    };
  }

  private normalizeCategoryColors(value: unknown): EventCategoryColorItem[] {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return [];
    }

    return Object.entries(value as Record<string, string>)
      .map(([category, color]) => ({
        category: category.trim(),
        color: (color || '#1976d2').trim() || '#1976d2',
      }))
      .filter((item) => item.category.length > 0);
  }

}
